<template>
  <SCAButtonGroup :class="[$style.split, { [$style.open]: active, [$style.disabled]: disabled }]">
    <SCAButton
      appearance="secondary"
      size="small"
      padding-left="8px"
      padding-right="6px"
      :title="leftTitle ?? title"
      :disabled="disabled"
      :popovertarget="mode === 'menu' ? popoverTarget : undefined"
      :test-id="testId ? `${testId}-left` : undefined"
      @click="mode !== 'menu' && $emit('action', $event)"
    >
      {{ $t("common.action.new") }}
    </SCAButton>
    <SCAButton
      appearance="secondary"
      size="small"
      icon-right="phosphor/caret-down"
      icon-size="12px"
      padding-left="4px"
      padding-right="4px"
      :title="rightTitle ?? title"
      :aria-label="(rightTitle ?? title) || $t('common.label.options')"
      :disabled="disabled"
      :popovertarget="mode === 'action' ? undefined : popoverTarget"
      :test-id="testId ? `${testId}-right` : undefined"
      @click="mode === 'action' && $emit('action', $event)"
    />
  </SCAButtonGroup>
</template>

<script setup lang="ts">
import { SCAButton, SCAButtonGroup } from "@scayla/components";
import { computed, useAttrs } from "vue";

/** The "New" split button. It is only ever disabled as a whole. */
withDefaults(
  defineProps<{
    /** `action`: both parts emit `action`; `menu`: both open the popover; `split`: "New" emits, the arrow opens the popover. */
    mode?: "action" | "menu" | "split";
    popoverTarget?: string;
    title?: string;
    /** Tooltips of "New" and the arrow when they do different things; `title` otherwise. */
    leftTitle?: string;
    rightTitle?: string;
    /** The popover the button opens is open. */
    active?: boolean;
    disabled?: boolean;
  }>(),
  {
    mode: "action",
    popoverTarget: undefined,
    title: "",
    leftTitle: undefined,
    rightTitle: undefined,
    active: false,
    disabled: false,
  },
);

defineEmits<{
  action: [event: MouseEvent];
}>();

// The component's own test-id stays on its root; each part gets one after it.
const attrs = useAttrs();
const testId = computed(() => attrs["test-id"] as string | undefined);
</script>

<style lang="scss" module>
// Hover, press and the open menu outline the whole button, divider included, whichever part is under the pointer.
// The tokens are remapped rather than the borders set, so SCAButton's own state rules draw them.
.split {
  &:has(> button:not(:disabled):hover) > button {
    --color-action-outline-normal: var(--color-action-outline-hover);
  }

  &:has(> button:not(:disabled):active) > button,
  &.open > button {
    --color-action-outline-normal: var(--color-action-outline-active);
    --color-action-outline-hover: var(--color-action-outline-active);
    --color-action-outline-selected: var(--color-action-outline-active);
  }
}

// Fades the button once: each part's own opacity would double up where the borders overlap.
.disabled {
  opacity: 0.5;

  > button[disabled],
  > button[disabled] svg {
    opacity: 1;
  }
}
</style>
